import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AbsenceItemComponent } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-items/absence/absence-item.component';
import { IconComponent as LegacyIconComponent } from '@app/+authenticated/shared/icon.component';
import { Features } from '@app/enums';
import { TimePipe } from '@app/pipes/time.pipe';
import { getAccountSubscription } from '@app/reducers/account/account.service';
import { AvailabilityRules, AvailabilityRulesService } from '@app/reducers/orm/availability/availability-rules.service';
import { AvailabilityType } from '@app/reducers/orm/availability/availability.model';
import { doPermissionCheck } from '@app/reducers/orm/permission/permission.service';
import { IsColorDarkPipe } from '@app/shared/helpers/is-color-dark.pipe';
import { LightenHexPipe } from '@app/shared/helpers/lighten-hex.pipe';
import { DatePagerComponent } from '@app/shared/sb-lib/calendar/date-pager/date-pager.component';
import { DatePipe } from '@app/shared/sb-lib/calendar/pipes/date.pipe';
import {
  hasAtleastSubscriptionPlan,
  SubscriptionPlanDirective,
} from '@app/shared/subscription-plan/subscription-plan.directive';
import { FeatureService } from '@app/startup/feature.service';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '@sb/tooltip';
import { ButtonComponent, DropdownModule, IconComponent, LoadingStateComponent } from '@sb/ui';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppState } from '../../../reducers/index';
import { ScheduleModel } from '../../../reducers/orm/schedule/schedule.model';
import { format, parseDate } from '../../../shared/date.helper';
import { lazySelect } from '../../../shared/lazy-select.observable';
import { PlanType } from '../../+reports/shared/subscriptions/subscription.model';
import { getMyScheduleData } from './personal-schedule.helper';
import { MyScheduleData, MyScheduleWeek } from './personal-schedule.interfaces';

@Component({
  selector: 'personal-schedule',
  templateUrl: './personal-schedule.component.html',
  standalone: true,
  imports: [
    LoadingStateComponent,
    DatePipe,
    AsyncPipe,
    ButtonComponent,
    IconComponent,
    LegacyIconComponent,
    TranslateModule,
    SubscriptionPlanDirective,
    DatePagerComponent,
    DropdownModule,
    TimePipe,
    NgClass,
    AbsenceItemComponent,
    TooltipModule,
    NgStyle,
    LightenHexPipe,
    IsColorDarkPipe,
  ],
})
export class PersonalScheduleComponent implements OnInit {
  public data?: MyScheduleData = null;

  private _date: Date = new Date();
  private dateChange$: BehaviorSubject<Date> = new BehaviorSubject(new Date());

  @Input({ required: true })
  public set employeeId(employeeId: string) {
    this._employeeId = employeeId;
    this.employeeId$.next(employeeId);
  }
  public get employeeId(): string {
    return this._employeeId;
  }
  private _employeeId: string;
  private employeeId$: BehaviorSubject<string> = new BehaviorSubject(null);

  @Input()
  public set date(date: Date) {
    this._date = date;
    this.dateChange$.next(date);
  }

  public get date(): Date {
    return this._date;
  }

  @Output()
  public changeDateEmitter = new EventEmitter<string>();

  public planType = PlanType;

  public AvailabilityType = AvailabilityType;

  public get firstEditableWeek(): MyScheduleWeek {
    return this.data?.firstEditableWeek;
  }

  public hasAtleastBasic$: Observable<boolean> = this.store
    .select(getAccountSubscription)
    .pipe(map((accountSubscription) => hasAtleastSubscriptionPlan(PlanType.BASIC, accountSubscription)));

  private readonly availabilityRules$: Observable<AvailabilityRules> = this.employeeId$.pipe(
    switchMap((employeeId) => this.availabilityRulesService.getUserRules(employeeId)),
  );

  private data$: Observable<MyScheduleData> = combineLatest([
    this.dateChange$,
    this.employeeId$,
    this.availabilityRules$,
  ]).pipe(
    switchMap(([date, employeeId, availabilityRules]) =>
      this.store.pipe(
        lazySelect(
          getMyScheduleData(
            employeeId,
            date,
            availabilityRules,
            this.feature.isEnabled(Features.TMP_AVAILABILITY_OPTIMISATION),
          ),
        ),
      ),
    ),
  );

  public canAddShifts$: Observable<boolean> = this.employeeId$.pipe(
    switchMap((employeeId) =>
      this.store.select(
        doPermissionCheck({
          permissions: ['Create roster', 'Create own roster'],
          userId: employeeId,
          departments: 'any',
        }),
      ),
    ),
  );

  public constructor(
    private store: Store<AppState>,
    private router: Router,
    private feature: FeatureService,
    private destroyRef: DestroyRef,
    private availabilityRulesService: AvailabilityRulesService,
  ) {}

  public ngOnInit() {
    void this.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.data = data;
    });
  }

  public changeDate(date: string) {
    this.date = parseDate(date);
    this.changeDateEmitter.emit(date);
  }

  public onEditAvailability(week: MyScheduleWeek) {
    if (!week.canEditAvailability) {
      return;
    }

    const start = format(week.firstEditableAvailabilityDate.date, 'yyyy-MM-dd');
    const end = format(week.lastDayOfWeek.date, 'yyyy-MM-dd');
    const params = { start, end, employee: this.employeeId };

    this.router.navigate(['', { outlets: { modal: ['availability', params] } }]);
  }

  public onEditShift(shift: ScheduleModel) {
    this.router.navigate([
      '',
      { outlets: { modal: ['schedule-shift', shift.occurrence_id, 'edit', { showEmployee: false }] } },
    ]);
  }

  public onDeleteShift(shift: ScheduleModel) {
    this.router.navigate(['', { outlets: { modal: ['schedule-shift', shift.occurrence_id, 'delete'] } }]);
  }

  public onViewShift(shift: ScheduleModel) {
    this.router.navigate(['', { outlets: { modal: ['schedule-shift', shift.occurrence_id] } }]);
  }

  public onAddShift() {
    this.router.navigate([
      '',
      { outlets: { modal: ['schedule-shift', { employee: this.employeeId, showEmployee: false }] } },
    ]);
  }

  public onCalendarSync() {
    this.router.navigate(['', { outlets: { modal: ['calendar-sync'] } }]);
  }
}
